/**
 * claimService.js — HTTP client for the Official Data Claim system.
 *
 * Endpoints map to src/modules/claims/api/routes_claims.py on the backend.
 * See SPEC_OFFICIAL_DATA_CLAIM.md for full specification.
 */
import { AZURE } from './api';

/* ─── Gestor Endpoints ─────────────────────────────────────────────────── */

/**
 * Search canonical clubs (swimming_clubs) by name for claiming.
 * @param {string} q - Search query (min 2 chars)
 * @returns {Promise<Array>} List of matching clubs with claim status info
 */
export const searchClubsForClaim = async (q) => {
  const { data } = await AZURE.get('/claims/search', { params: { q } });
  return data.data || [];
};

/**
 * Request a claim linking an institution to a canonical results club.
 * @param {Object} payload - { institution_id, results_club_id, evidence_type, evidence_notes }
 * @returns {Promise<Object>} { claim_id, status, confidence_score }
 */
export const requestClaim = async (payload) => {
  const { data } = await AZURE.post('/claims/request', {
    institution_id: payload.institution_id,
    results_club_id: payload.results_club_id,
    evidence_type: payload.evidence_type,
    evidence_notes: payload.evidence_notes || payload.evidence_detail || '',
  });
  return data;
};

/**
 * Get all claims for the current user's institution.
 * @param {number} institutionId
 * @returns {Promise<Object>} Claim data or null
 */
export const getMyClaims = async (institutionId) => {
  const { data } = await AZURE.get('/claims/my', {
    params: { institution_id: institutionId }
  });
  return data;
};

/**
 * Submit additional evidence for a claim that needs more info.
 * @param {number} claimId
 * @param {Object} payload - { evidence_type, evidence_detail }
 * @returns {Promise<Object>}
 */
export const submitEvidence = async (claimId, payload) => {
  const { data } = await AZURE.put(`/claims/${claimId}/evidence`, {
    evidence_type: payload.evidence_type,
    evidence_notes: payload.evidence_notes || payload.evidence_detail || '',
  });
  return data;
};

/* ─── Admin Endpoints ──────────────────────────────────────────────────── */

/**
 * Get all pending claims for admin review.
 * @returns {Promise<Array>}
 */
export const getPendingClaims = async () => {
  const { data } = await AZURE.get('/claims/admin/pending');
  return data.data || [];
};

/**
 * Get full detail of a specific claim (including audit log).
 * @param {number} claimId
 * @returns {Promise<Object>} { claim, audit_log }
 */
export const getClaimDetail = async (claimId) => {
  const { data } = await AZURE.get(`/claims/admin/${claimId}`);
  return data.data || {};
};

/**
 * Approve a claim.
 * @param {number} claimId
 * @param {string} [notes] - Optional review notes
 * @returns {Promise<Object>}
 */
export const approveClaim = async (claimId, notes = '') => {
  const { data } = await AZURE.put(`/claims/admin/${claimId}/approve`, { notes });
  return data;
};

/**
 * Reject a claim (rejection_reason required).
 * @param {number} claimId
 * @param {string} rejectionReason
 * @param {string} [notes]
 * @returns {Promise<Object>}
 */
export const rejectClaim = async (claimId, rejectionReason, notes = '') => {
  const { data } = await AZURE.put(`/claims/admin/${claimId}/reject`, {
    rejection_reason: rejectionReason,
    notes,
  });
  return data;
};

/**
 * Request more info from the gestor.
 * @param {number} claimId
 * @param {string} [notes]
 * @returns {Promise<Object>}
 */
export const requestMoreInfo = async (claimId, notes = '') => {
  const { data } = await AZURE.put(`/claims/admin/${claimId}/request-info`, { notes });
  return data;
};

/**
 * Revoke a previously approved claim.
 * @param {number} claimId
 * @param {string} revokedReason
 * @param {string} [notes]
 * @returns {Promise<Object>}
 */
export const revokeClaim = async (claimId, revokedReason, notes = '') => {
  const { data } = await AZURE.put(`/claims/admin/${claimId}/revoke`, {
    revoked_reason: revokedReason,
    notes,
  });
  return data;
};

/* ─── Claim Status Helpers ─────────────────────────────────────────────── */

export const CLAIM_STATUSES = {
  not_requested: { label: 'Não solicitado', color: '#6b7280', bg: '#f3f4f6' },
  requested: { label: 'Solicitado', color: '#d97706', bg: '#fef3c7' },
  under_review: { label: 'Em análise', color: '#2563eb', bg: '#dbeafe' },
  needs_more_info: { label: 'Informação adicional', color: '#ea580c', bg: '#fed7aa' },
  approved: { label: 'Aprovado', color: '#16a34a', bg: '#dcfce7' },
  rejected: { label: 'Rejeitado', color: '#dc2626', bg: '#fecaca' },
  revoked: { label: 'Revogado', color: '#dc2626', bg: '#fecaca' },
};

export const EVIDENCE_TYPES = [
  { value: 'cnpj', label: 'CNPJ do clube' },
  { value: 'institutional_email', label: 'E-mail institucional' },
  { value: 'document', label: 'Documento oficial (ata, registro)' },
  { value: 'verbal', label: 'Declaração verbal / contato direto' },
  { value: 'none', label: 'Nenhuma evidência no momento' },
];
